import React from 'react';
import {
  ElasticSearch,
  ElasticSearchFacetsFields,
  ElasticSearchFilterKeys,
  ElasticSearchStringListFilterProperties,
  isPriceFilterKey,
} from '@interfaces/models/elasticSearch';
import { facetToFilterRename, filterToFacetRename } from '@maps/catalog/search-product-facets-filters-maps';
import { isBooleanFilterKey, isStringListFilterKey } from '@constants/catalog';
import {
  ElasticSearchFacet,
  ElasticSearchFacetFieldResponse,
  ElasticSearchResponse,
} from '@interfaces/api/responses/elasticSearch';
import { usePreferences } from '@context/preferences.context';
import { chainMethods } from '@helpers/utils/general';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { StaticFilterGroups } from '@hooks/catalog-context/use-static-filter-groups';
import useMarketingFiltersQuery from '@hooks/marketing-filters/use-marketing-filters-query';
import { getApplyFilterPayload } from '@helpers/utils/catalog/catalog-tracking-utils';
import { FilterSource } from '@enums/filter-source';
import { FilterSources } from '@interfaces/models/filter-sources';
import { SourceAddition, syncFilterSources } from '@helpers/utils/catalog/filter-sources-helpers';

interface UpdateFiltersCallbacks {
  isManualSwitchRef: React.MutableRefObject<boolean>;
  filters: ElasticSearch['filters'];
  setFilters: React.Dispatch<React.SetStateAction<ElasticSearch['filters']>>;
  filterSources: FilterSources;
  setFilterSources: React.Dispatch<React.SetStateAction<FilterSources>>;
  facets: ElasticSearchResponse['facets']['fields'];
  areMySizesEnabled: boolean;
  applicableSizeIds: number[];
  staticFilterGroups: StaticFilterGroups;
}

const getAnalyticsCompliantFilterValue = ({
  filterGroup,
  filterName,
  filterIds,
}: {
  filterGroup: ElasticSearchFacetsFields;
  filterIds?: string[];
  filterName?: string;
}): string => {
  switch (filterGroup) {
    case 'stock':
      return '1';
    case 'sold':
      return filterName;
    case 'localCountries':
      return 'true';
    case 'directShippingEligible':
      return filterName;
    default:
      return filterIds && filterIds[0] ? String(filterIds[0]) : '';
  }
};

const removeFilterGroup = (filters: ElasticSearch['filters'], key: ElasticSearchFilterKeys, condition = true) => {
  if (condition) {
    const { [key]: removed, ...finalState } = filters || {};
    return finalState;
  }
  return filters;
};

const removeChildFilters = ({
  filters,
  parentKey,
  childKey,
  filterGroupName,
  filterIds,
  facets,
}: {
  filters: ElasticSearch['filters'];
  parentKey: ElasticSearchStringListFilterProperties;
  childKey: ElasticSearchStringListFilterProperties;
  filterGroupName: ElasticSearchStringListFilterProperties;
  filterIds?: string[];
  facets: ElasticSearchFacetFieldResponse;
}) => {
  const parentID = filterIds?.[0];
  if (filterGroupName !== parentKey || !parentID) {
    return filters;
  }
  const childFacetKey = filterToFacetRename(childKey);

  if (filters.hasOwnProperty(childKey) && facets.hasOwnProperty(childFacetKey)) {
    const facetValues = facets[childFacetKey];

    const newState: ElasticSearch['filters'] = {
      ...filters,
      ...{
        [childKey]: filters[childKey].filter((filter) => {
          const facet = facetValues.find((facet) => String(facet.id) === String(filter));
          return facet && String(facet.parentID) !== String(parentID);
        }),
      },
    };

    // If there are still ids for a filterGroup selected, keep the property
    if (newState[childKey].length > 0) {
      return newState;
    }

    return removeFilterGroup(newState, childKey);
  }
  return filters;
};

type SendTrackingEvent = (filterType?: string) => void;

const useUpdateFilters = ({
  isManualSwitchRef,
  filters,
  setFilters,
  filterSources,
  setFilterSources,
  facets,
  staticFilterGroups,
  areMySizesEnabled,
  applicableSizeIds,
}: UpdateFiltersCallbacks) => {
  const { sendAnalyticEvent } = useAnalyticEvents('catalog');
  const { currency } = usePreferences();
  const { localToLocalFilter, freeShippingFilter } = useMarketingFiltersQuery();

  // filter_details snapshots the user-applied filter state BEFORE this click (closure values),
  // so it represents the previous state; filters_applied carries the just-applied delta.
  const sendSnowplowEvent = (filters_applied: { [key: string]: string }) => {
    const payload = getApplyFilterPayload(filters, filters_applied, filterSources);
    sendAnalyticEvent('apply_filter', payload);
  };

  // Apply a filter mutation: write filters, sync sources (prune orphans + layer additions), commit both.
  const commit = (newFilters: ElasticSearch['filters'], additions: SourceAddition[] = []): FilterSources => {
    const newSources = syncFilterSources(newFilters, filterSources, additions);
    setFilters(newFilters);
    setFilterSources(newSources);
    return newSources;
  };

  /***
  START
  HELPER METHODS FOR HANDLING TOGGLE FILTERS
   *****/
  const removePriceChipFilter = (filterIds?: string[]) => {
    const currentPriceFilter = filters.price || {};

    if (isPriceFilterKey(filterIds[0]) && currentPriceFilter.hasOwnProperty(filterIds[0])) {
      const { [filterIds[0]]: removed, ...newPrice } = currentPriceFilter;

      // if all price filters are removed, remove the price filter from the filters object
      if (Object.keys(newPrice).length === 0) {
        commit(removeFilterGroup(filters, 'price'));
        return;
      }
      commit({
        ...filters,
        ...{ price: newPrice },
      });
    }
  };

  const filterFreeShipping = (sendTrackingEvent: SendTrackingEvent, isChecked: boolean) => {
    if (isChecked) {
      const newState = chainMethods<ElasticSearch['filters']>(
        (f: ElasticSearch['filters']) => removeFilterGroup(f, 'price'),
        (f: ElasticSearch['filters']) => removeFilterGroup(f, 'country'),
        (f: ElasticSearch['filters']) => removeFilterGroup(f, 'localCountries'),
      )(filters);
      commit(newState);
      return;
    }
    const newState: ElasticSearch['filters'] = {
      ...filters,
      localCountries: true,
      price: { '>=': 20000 },
      country: ['US'],
    };
    commit(newState, [
      { key: 'localCountries', values: ['true'], source: FilterSource.MarketingFilter },
      { key: 'price', values: ['>='], source: FilterSource.MarketingFilter },
      { key: 'country', values: ['US'], source: FilterSource.MarketingFilter },
    ]);
    sendTrackingEvent();
  };

  const filterLocalCountries = (sendTrackingEvent: SendTrackingEvent) => {
    const localCountriesList = localToLocalFilter?.attributes.countries;
    const country = filters?.country?.filter((isoCode) => localCountriesList.includes(isoCode));
    const newState: ElasticSearch['filters'] = {
      ...filters,
      localCountries: true,
      ...{ country: country },
    };
    commit(newState, [
      { key: 'localCountries', values: ['true'], source: FilterSource.MarketingFilter },
      { key: 'country', values: (country ?? []).map(String), source: FilterSource.MarketingFilter },
    ]);
    sendTrackingEvent();
  };

  const handleDirectShippingFilter = (sendTrackingEvent: SendTrackingEvent) => {
    if (filters.hasOwnProperty('directShippingCountries') && filters.hasOwnProperty('directShippingEligible')) {
      const { directShippingCountries, directShippingEligible, ...newState } = filters;

      commit(newState);
      return;
    }

    const newState: ElasticSearch['filters'] = {
      ...filters,
      directShippingCountries: true,
      // TODO: This can be removed when FENX go 100% on prod now we need to have this to handle links and filter that come from ngx
      directShippingEligible: true,
    };

    commit(newState, [
      { key: 'directShippingCountries', values: ['true'], source: FilterSource.User },
      { key: 'directShippingEligible', values: ['true'], source: FilterSource.User },
    ]);
    sendTrackingEvent('dseligiblecountries');
  };

  const handleBooleanFilter = (filterGroupName: ElasticSearchFilterKeys, sendTrackingEvent: SendTrackingEvent) => {
    // Remove boolean property
    if (filters.hasOwnProperty(filterGroupName)) {
      const newState = removeFilterGroup(filters, filterGroupName);
      commit(newState);
      return;
    }

    const newState: ElasticSearch['filters'] = {
      ...filters,
      [filterGroupName]: true,
    };

    commit(newState, [{ key: filterGroupName, values: ['true'], source: FilterSource.User }]);
    sendTrackingEvent();
  };

  const uncheckStringListFilter = (
    filterGroupName: ElasticSearchStringListFilterProperties,
    filterIds?: string[],
    filterName?: string,
  ) => {
    const baseState: ElasticSearch['filters'] = {
      ...filters,
      [filterGroupName]: (filters[filterGroupName] ?? []).filter((filter) => !filterIds.includes(filter)),
    };

    // Uncheck all models with the same name
    if (filterGroupName === 'model.id') {
      const modelsToUncheck = facets.model
        .filter((model: ElasticSearchFacet) => model.name === filterName)
        .map((model: ElasticSearchFacet) => model.id);

      const remainingModels = baseState['model.id'].filter((modelId: string) => !modelsToUncheck.includes(modelId));

      if (remainingModels.length) {
        commit({ ...filters, 'model.id': remainingModels });
        return;
      }
      commit(removeFilterGroup(baseState, 'model.id'));
      return;
    }

    // If there are still ids for a filterGroup selected, keep the property
    if (baseState[filterGroupName].length > 0) {
      const finalState = chainMethods<ElasticSearch['filters']>(
        // remove child filters for materialLvl0
        (f) =>
          removeChildFilters({
            filters: f,
            parentKey: 'materialLvl0.id',
            childKey: 'materialLvl1.id',
            filterGroupName,
            filterIds,
            facets,
          }),
        // TODO: remove child filters for category and designer filters also!
      )(baseState);
      commit(finalState);
      return;
    }

    const modifiedState = chainMethods<ElasticSearch['filters']>(
      // if final categoryLvl1 is unchecked, remove categoryLvl2
      (f) =>
        removeFilterGroup(
          f,
          'categoryLvl2.id',
          filterGroupName === 'categoryLvl1.id' && f.hasOwnProperty('categoryLvl2.id'),
        ),
      // if final categoryLvl0 is unchecked, remove categoryLvl1
      (f) =>
        removeFilterGroup(
          f,
          'categoryLvl1.id',
          filterGroupName === 'categoryLvl0.id' && f.hasOwnProperty('categoryLvl1.id'),
        ),
      // if final materialLvl0 is unchecked, remove materialLvl1
      (f) =>
        removeFilterGroup(
          f,
          'materialLvl1.id',
          filterGroupName === 'materialLvl0.id' && f.hasOwnProperty('materialLvl1.id'),
        ),
      // if final universe is unchecked, remove categoryLvl0, categoryLvl1 and categoryLvl2
      (f) =>
        removeFilterGroup(
          f,
          'categoryLvl2.id',
          filterGroupName === 'universe.id' && f.hasOwnProperty('categoryLvl2.id'),
        ),
      (f) =>
        removeFilterGroup(
          f,
          'categoryLvl1.id',
          filterGroupName === 'universe.id' && f.hasOwnProperty('categoryLvl1.id'),
        ),
      (f) =>
        removeFilterGroup(
          f,
          'categoryLvl0.id',
          filterGroupName === 'universe.id' && f.hasOwnProperty('categoryLvl0.id'),
        ),
      // finally remove the filterGroup since it's empty
      (f) => removeFilterGroup(f, filterGroupName),
    )(baseState);

    commit(modifiedState);
  };

  const checkModelFilters = (sendTrackingEvent: SendTrackingEvent, modelIds: string[], filterName?: string) => {
    const selectedModels = facets.model.filter((model) => model.name === filterName).map((model) => model.id);
    const modelSet = new Set([...selectedModels, ...modelIds]);
    const addedValues = Array.from(modelSet).map(String);
    const newState = {
      ...filters,
      // model.id may not exist, so create an empty array in that case
      'model.id': [...(filters['model.id' as ElasticSearchStringListFilterProperties] ?? []), ...modelSet],
    };

    commit(newState, [{ key: 'model.id', values: addedValues, source: FilterSource.User }]);

    sendTrackingEvent();
  };
  /***
   END
   HELPER METHODS FOR HANDLING TOGGLE FILTERS
   *****/

  // This is the main function that handles the toggling of filters
  const toggleFilter = (filterGroup: ElasticSearchFacetsFields, filterIds?: string[], filterName?: string): void => {
    isManualSwitchRef.current = true;
    const sendTrackingEvent: SendTrackingEvent = (filterType = facetToFilterRename(filterGroup)) => {
      const filterValue = getAnalyticsCompliantFilterValue({
        filterGroup,
        filterIds,
        filterName,
      });
      sendSnowplowEvent({ [filterType]: filterValue });
    };

    // this is responsible for handing the removal of the price chip - setting the price filter is done elsewhere
    if (filterGroup === 'price') {
      removePriceChipFilter(filterIds);
      return;
    }

    if (filterGroup === 'directShippingEligible') {
      handleDirectShippingFilter(sendTrackingEvent);
      return;
    }

    // Some filterGroups must be renamed from their facet naming, to comply with the API
    const filterGroupName = facetToFilterRename(filterGroup);

    if (filterGroup === 'localCountries' && !!freeShippingFilter) {
      const isChecked = filters.hasOwnProperty('localCountries');
      filterFreeShipping(sendTrackingEvent, isChecked);
      return;
    }

    // If the user has activated the 'local country' filter, maps the country filter to the specified list
    if (filterGroup === 'localCountries' && filters.country && !filters.localCountries) {
      filterLocalCountries(sendTrackingEvent);
      return;
    }

    // For boolean properties, we just toggle between them being present or not
    if (isBooleanFilterKey(filterGroupName)) {
      handleBooleanFilter(filterGroupName, sendTrackingEvent);
      return;
    }

    // Non-boolean properties, the 'as' is fine, because we filtered out booleans earlier
    const isChecked: boolean = !!filters[filterGroupName as ElasticSearchStringListFilterProperties]?.find((filter) =>
      filterIds.includes(filter),
    );

    // Uncheck if checked
    if (isChecked && isStringListFilterKey(filterGroupName)) {
      uncheckStringListFilter(filterGroupName, filterIds, filterName);
      return;
    }

    // Check all models with the same name
    if (filterGroupName === 'model.id') {
      checkModelFilters(sendTrackingEvent, filterIds, filterName);
      return;
    }

    const newState = {
      ...filters,
      // ...prevState[filterGroup] may not exist, so create an empty array in that case
      [filterGroupName]: [...(filters[filterGroupName as ElasticSearchStringListFilterProperties] ?? []), ...filterIds],
    };

    commit(newState, [{ key: filterGroupName, values: filterIds.map(String), source: FilterSource.User }]);

    sendTrackingEvent();
  };

  const removeAllFilters = (): void => {
    const prevState = filters || {};
    const newFilterState: ElasticSearch['filters'] = {};

    // keep catalogLinksWithoutLanguage
    if (prevState.hasOwnProperty('catalogLinksWithoutLanguage')) {
      newFilterState.catalogLinksWithoutLanguage = prevState.catalogLinksWithoutLanguage;
    }

    // keep campaign filter
    if (prevState.hasOwnProperty('campaign.id')) {
      newFilterState['campaign.id'] = prevState['campaign.id'];
    }

    // keep static filters
    Object.keys(staticFilterGroups).forEach((filterGroup: ElasticSearchFacetsFields) => {
      const filterKey: string = facetToFilterRename(filterGroup);
      if (staticFilterGroups[filterGroup] && prevState[filterKey]) {
        newFilterState[filterKey] = prevState[filterKey];
      }
    });

    // if we are on multi-universe page and my sizes is not enabled, remove the universe filter also
    let finalState: ElasticSearch['filters'] = newFilterState;
    if (applicableSizeIds.length > 1 && !areMySizesEnabled && newFilterState['universe.id']) {
      const { 'universe.id': removed, ...rest } = newFilterState;
      finalState = rest;
    }

    commit(finalState);
  };

  const updatePriceRangeFilter = (min: number, max: number): void => {
    isManualSwitchRef.current = true;
    const price: ElasticSearch['filters']['price'] = {
      ...(min > 0 && {
        '>=': min,
      }),
      ...(max > 0 && {
        '<=': max,
      }),
    };
    const newState = {
      ...filters,
      price,
    };
    commit(newState, [{ key: 'price', values: Object.keys(price), source: FilterSource.User }]);
    const minFilterValue = price['>='] ? `min:${price['>=']}${currency}` : '';
    const maxFilterValue = price['<='] ? `max:${price['<=']}${currency}` : '';
    const filterPriceValue =
      (minFilterValue !== '' && maxFilterValue !== '' ? `${minFilterValue}_` : minFilterValue) + maxFilterValue;

    sendSnowplowEvent({ price: filterPriceValue });
  };

  return { toggleFilter, removeAllFilters, updatePriceRangeFilter };
};

export default useUpdateFilters;
